/* Dividend aggregates.
 *
 * A single payment is trivia. The point of recording them is what they add up
 * to across years — which is why almost everything here is a series over time
 * rather than a total, and why yield is measured against what was paid for the
 * shares rather than against today's price. */

import type { Dividend, FxRates } from '../types';
import { toUsd } from './derive';

export interface YearTotal { year: number; grossUsd: number; netUsd: number; taxUsd: number; count: number }

/* What a set of payments came to, before and after what was taken off them.
 *
 * Three figures rather than one, because the record already holds all three and
 * showing only the last of them answers half the question. `grossUsd` is what
 * the company declared, `withheldUsd` is what the payer's country took before
 * it arrived, and `netUsd` is what actually landed.
 *
 * What is still owed after that is a separate figure and lives on the screen,
 * not here, because it depends on a rate the user sets rather than on anything
 * recorded. */
export interface DividendSplit {
  grossUsd: number;
  withheldUsd: number;
  netUsd: number;
  count: number;
}

const emptySplit = (): DividendSplit => ({ grossUsd: 0, withheldUsd: 0, netUsd: 0, count: 0 });

export interface MonthTotal { month: string; netUsd: number; count: number }   // 'YYYY-MM'

export interface TickerTotal {
  ticker: string;
  netUsd: number;
  grossUsd: number;
  taxUsd: number;
  count: number;
  firstPayDate: string;
  lastPayDate: string;
  /** Net received over the last twelve months against what the shares cost.
      Null when the position's cost is not known. */
  yieldOnCostPct: number | null;
  trailingTwelveUsd: number;
}

export interface DividendSummary {
  paymentCount: number;
  totalGrossUsd: number;
  totalNetUsd: number;
  totalTaxUsd: number;
  firstPayDate: string | null;
  lastPayDate: string | null;
  /** Years between the first payment and now, to one decimal. */
  yearsRunning: number;
  byYear: YearTotal[];
  byMonth: MonthTotal[];
  byTicker: TickerTotal[];
  /** Net received in the last 365 days. */
  trailingTwelveUsd: number;
  /** Trailing twelve against the cost of the shares that paid it. */
  yieldOnCostPct: number | null;
  /** Average per month across the whole record, not just the months that paid. */
  monthlyAverageUsd: number;
  /** Best guess at the next twelve months: each holding's most recent full year
      of payments, scaled to the shares held now. */
  forwardAnnualUsd: number;
  /** The same record split by where the shares were held, because what is still
      owed on it turns entirely on that. */
  sheltered: DividendSplit;
  taxable: DividendSplit;
}

const DAY = 86_400_000;

function netUsdOf(d: Dividend, fx: FxRates | undefined): number {
  return d.rateToUsd !== undefined ? d.net * d.rateToUsd : toUsd(d.net, d.currency, fx) ?? 0;
}

function grossUsdOf(d: Dividend, fx: FxRates | undefined): number {
  return d.rateToUsd !== undefined ? d.gross * d.rateToUsd : toUsd(d.gross, d.currency, fx) ?? 0;
}

function monthKey(iso: string): string { return iso.slice(0, 7); }

/** Every month between two dates inclusive, so a month that paid nothing still
    appears as a zero rather than being skipped — a gap in dividend income is
    information, and collapsing it would hide it. */
function monthRange(from: string, to: string): string[] {
  const out: string[] = [];
  const start = new Date(`${from.slice(0, 7)}-01T00:00:00Z`);
  const end = new Date(`${to.slice(0, 7)}-01T00:00:00Z`);
  const cursor = new Date(start);
  while (cursor <= end) {
    out.push(cursor.toISOString().slice(0, 7));
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return out;
}

export interface DividendInput {
  dividends: Dividend[];
  fx: FxRates | undefined;
  /** Cost basis in USD per ticker, for yield on cost. */
  costByTicker: Map<string, number>;
  /** Shares held now per ticker, for the forward estimate. */
  sharesByTicker: Map<string, number>;
  /* Accounts whose gains and dividends sit outside the tax base — an IKE, for
     one. A payment into one of these is finished when it lands; a payment into
     any other account still has the local charge to come.
     *
     * An account this set does not name counts as taxable, including one that
     * has since been deleted from the book. That is the safe direction: the
     * error is a figure that says more is owed than is, which somebody checks,
     * rather than one that says nothing is owed. */
  shieldedAccounts?: Set<string>;
  now?: Date;
}

export function summariseDividends(input: DividendInput): DividendSummary {
  const { dividends, fx, costByTicker, sharesByTicker } = input;
  const shielded = input.shieldedAccounts ?? new Set<string>();
  const now = input.now ?? new Date();

  const empty: DividendSummary = {
    paymentCount: 0, totalGrossUsd: 0, totalNetUsd: 0, totalTaxUsd: 0,
    firstPayDate: null, lastPayDate: null, yearsRunning: 0,
    byYear: [], byMonth: [], byTicker: [],
    trailingTwelveUsd: 0, yieldOnCostPct: null, monthlyAverageUsd: 0, forwardAnnualUsd: 0,
    sheltered: emptySplit(), taxable: emptySplit(),
  };
  if (dividends.length === 0) return empty;

  const sorted = [...dividends].sort((a, b) => a.payDate.localeCompare(b.payDate));
  const first = sorted[0]!.payDate;
  const last = sorted[sorted.length - 1]!.payDate;
  const twelveMonthsAgo = new Date(now.getTime() - 365 * DAY).toISOString().slice(0, 10);

  let totalGrossUsd = 0;
  let totalNetUsd = 0;
  let trailingTwelveUsd = 0;

  const sheltered = emptySplit();
  const taxable = emptySplit();

  const years = new Map<number, YearTotal>();
  const months = new Map<string, MonthTotal>();
  const tickers = new Map<string, TickerTotal & { _grossUsd: number }>();

  for (const d of sorted) {
    const net = netUsdOf(d, fx);
    const gross = grossUsdOf(d, fx);
    const tax = gross - net;
    totalNetUsd += net;
    totalGrossUsd += gross;
    if (d.payDate >= twelveMonthsAgo) trailingTwelveUsd += net;

    const bucket = shielded.has(d.account) ? sheltered : taxable;
    bucket.grossUsd += gross;
    bucket.withheldUsd += tax;
    bucket.netUsd += net;
    bucket.count += 1;

    const year = Number(d.payDate.slice(0, 4));
    const y = years.get(year) ?? { year, grossUsd: 0, netUsd: 0, taxUsd: 0, count: 0 };
    y.grossUsd += gross; y.netUsd += net; y.taxUsd += tax; y.count += 1;
    years.set(year, y);

    const m = months.get(monthKey(d.payDate)) ?? { month: monthKey(d.payDate), netUsd: 0, count: 0 };
    m.netUsd += net; m.count += 1;
    months.set(m.month, m);

    const t = tickers.get(d.ticker) ?? {
      ticker: d.ticker, netUsd: 0, grossUsd: 0, _grossUsd: 0, taxUsd: 0, count: 0,
      firstPayDate: d.payDate, lastPayDate: d.payDate,
      yieldOnCostPct: null, trailingTwelveUsd: 0,
    };
    t.netUsd += net; t.grossUsd += gross; t.taxUsd += tax; t.count += 1;
    t.lastPayDate = d.payDate;
    if (d.payDate >= twelveMonthsAgo) t.trailingTwelveUsd += net;
    tickers.set(d.ticker, t);
  }

  for (const t of tickers.values()) {
    const cost = costByTicker.get(t.ticker);
    t.yieldOnCostPct = cost && cost > 0 ? (t.trailingTwelveUsd / cost) * 100 : null;
  }

  /* Forward estimate: the last full year of per-share payments for each ticker,
     applied to the shares held now. It assumes the payout holds — which is an
     assumption, so the screen labels it as one. */
  let forwardAnnualUsd = 0;
  for (const [ticker, t] of tickers) {
    const shares = sharesByTicker.get(ticker) ?? 0;
    if (shares <= 0) continue;
    const lastYear = sorted.filter((d) => d.ticker === ticker && d.payDate >= twelveMonthsAgo);
    const perShare = lastYear.reduce((sum, d) => sum + d.perShare, 0);
    const rate = lastYear[0]?.rateToUsd;
    const usd = rate !== undefined
      ? perShare * shares * rate
      : toUsd(perShare * shares, lastYear[0]?.currency ?? 'USD', fx) ?? 0;
    forwardAnnualUsd += usd;
    void t;
  }

  const allMonths = monthRange(first, now.toISOString().slice(0, 10));
  const byMonth = allMonths.map((month) => months.get(month) ?? { month, netUsd: 0, count: 0 });

  const firstMs = new Date(`${first}T00:00:00Z`).getTime();
  const yearsRunning = Math.max(0, (now.getTime() - firstMs) / (365.25 * DAY));

  const totalCost = [...tickers.keys()]
    .reduce((sum, ticker) => sum + (costByTicker.get(ticker) ?? 0), 0);

  return {
    paymentCount: sorted.length,
    totalGrossUsd,
    totalNetUsd,
    totalTaxUsd: totalGrossUsd - totalNetUsd,
    firstPayDate: first,
    lastPayDate: last,
    yearsRunning: Math.round(yearsRunning * 10) / 10,
    byYear: [...years.values()].sort((a, b) => a.year - b.year),
    byMonth,
    byTicker: [...tickers.values()]
      .map(({ _grossUsd, ...t }) => t)
      .sort((a, b) => b.netUsd - a.netUsd),
    trailingTwelveUsd,
    yieldOnCostPct: totalCost > 0 ? (trailingTwelveUsd / totalCost) * 100 : null,
    // Averaged over every month since the first payment, including the dry ones.
    monthlyAverageUsd: byMonth.length > 0 ? totalNetUsd / byMonth.length : 0,
    forwardAnnualUsd,
    sheltered,
    taxable,
  };
}
